import {Box, Button, Center, createListCollection, Field, Heading, Input, ListCollection, Select, Show, SimpleGrid, Stack, Table, Tabs, Text} from "@chakra-ui/react";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useRecoilState} from "recoil";
import {companyData, companyOperatorsData, userLoginResultData} from "../../components/hooks/recoilData";
import {useToast} from "@chakra-ui/toast";
import {Account, CompanyFile, CompanyOperator, CompanyOperatorRoleType, dataApi} from "../../api/data-api";
import {LuCoins, LuInfo, LuLibrary, LuSettings, LuUsers} from "react-icons/lu";
import {checkAccess, errorNotify} from "../../components/hooks/CommonUtils";
import {DateTime} from "luxon";
import {Profile} from "../../api/user-api";

interface CompanyExt {
    id: number | null,
    name: string,
    nameFull: string,
    address: string,
    addressActual: string,
    codeInn: string,
    codeKPP: string,
    codeOGRN: string,
    email: string,
    tel: string,
}

interface CompanyOperatorEx {
    id: number | null,
    userId: number | null,
    roleType: CompanyOperatorRoleType,
    codeInn: string,
}

export const companyOperatorRoleTypeList = createListCollection({
    items: [
        {label: "READ", value: "READ"},
        {label: "EDIT", value: "EDIT"},
        {label: "EDIT_AND_CONFIRM", value: "EDIT_AND_CONFIRM"},
        {label: "OWNER", value: "OWNER"},
    ],
});

export const CompanyInfo = () => {
    const toast = useToast();
    const [company] = useRecoilState(companyData);
    const [userLoginResult] = useRecoilState(userLoginResultData);
    const [companyOperators, setCompanyOperators] = useRecoilState(companyOperatorsData);
    const [files, setFiles] = useState<CompanyFile[]>([]);
    const [showFormOperator, setShowFormOperator] = useState<boolean>(false);
    const inputDefaultOperator: CompanyOperatorEx = useMemo(() => {
        return {
            id: null,
            userId: null,
            roleType: "READ",
            codeInn: "",
        }
    }, []);
    const [inputParamsOperator, setInputParamsOperator] = useState<CompanyOperatorEx>(inputDefaultOperator);
    const [usersForOperator, setUsersForOperator] = useState<Profile[]>([]);
    const [usersForOperatorList, setUsersForOperatorList] = useState<ListCollection>(createListCollection({items: [{label: "", value: ""},]}));
    const [companyOperatorList, setCompanyOperatorList] = useState<CompanyOperator[]>([]);

    const inputDefault: CompanyExt = useMemo(() => {
        return {
            id: null,
            name: "",
            nameFull: "",
            address: "",
            addressActual: "",
            codeInn: "",
            codeKPP: "",
            codeOGRN: "",
            email: "",
            tel: ""
        }
    }, []);
    const [inputParams, setInputParams] = useState<CompanyExt>(inputDefault);
    useEffect(() => {
        if (company) {
            setInputParams({
                id: company.ID,
                name: company.NAME,
                nameFull: company.NAME_FULL ? company.NAME_FULL : company.NAME,
                address: company.ADDRESS,
                addressActual: company.ADDRESS_ACTUAL ? company.ADDRESS_ACTUAL : company.ADDRESS,
                codeInn: company.CODE_INN.trim(),
                codeKPP: company.CODE_KPP ? company.CODE_KPP.trim() : "",
                codeOGRN: company.CODE_OGRN ? company.CODE_OGRN.trim() : "",
                email: company.EMAIL.trim(),
                tel: company.TEL.trim(),
            });
            dataApi.companyFileList(company.ID)
                .then(o => {
                    if (o)
                        setFiles(o);
                })
                .catch((err) => errorNotify(err, toast));
            dataApi.usersOperatorReadyList(company.ID)
                .then((o) => {
                    setUsersForOperator(o);
                    let arr = [{label: "", value: ""}];
                    arr.push(...o.map(it => {
                        return {label: it.FIRST_NAME + ' ' + it.SECOND_NAME + ' ' + it.LAST_NAME + ' ' + it.CODE_INN, value: String(it.USER_ID)}
                    }));
                    setUsersForOperatorList(createListCollection({
                        items: arr
                    }));
                })
                .catch((err) => errorNotify(err, toast));
            dataApi.companyOperatorList(company.ID)
                .then((o) => {
                    setCompanyOperatorList(o);
                })
                .catch((err) => errorNotify(err, toast));

        }
    }, [toast, company]);

    const save = useCallback(() => {
        if (!inputParams.id)
            return;
        dataApi.companySave(inputParams.id, inputParams.address, inputParams.addressActual, inputParams.email, inputParams.tel)
            .then((o) => {
                if (o) {
                    toast({title: "Save", status: "success"});
                    setInputParams({
                        id: o.ID,
                        name: o.NAME,
                        nameFull: o.NAME_FULL ? o.NAME_FULL : o.NAME,
                        address: o.ADDRESS,
                        addressActual: o.ADDRESS_ACTUAL ? o.ADDRESS_ACTUAL : o.ADDRESS,
                        codeInn: o.CODE_INN.trim(),
                        codeKPP: o.CODE_KPP ? o.CODE_KPP.trim() : "",
                        codeOGRN: o.CODE_OGRN ? o.CODE_OGRN.trim() : "",
                        email: o.EMAIL,
                        tel: o.TEL,
                    });
                } else {
                    toast({title: "Save failed", status: "error"});
                }
            })
            .catch((err) => errorNotify(err, toast));
    }, [inputParams, toast]);

    let checkAccessOwner = checkAccess(userLoginResult, company, companyOperators, "OWNER");
    // let checkAccessRead = checkAccess(userLoginResult, company, companyOperators, "READ");

    const [account, setAccount] = useState<Account>();
    useEffect(() => {
        if (userLoginResult) {
            if (company && (userLoginResult.isEmployer || userLoginResult.isOperator)) {
                dataApi.accountGet(company.ID)
                    .then((o2) => {
                        setAccount(o2);
                    })
                    .catch((err) => {
                        console.log("Login error status code is: " + err)
                    });
            }
        }
    }, [company, userLoginResult]);

    const fileDownload = useCallback((it: CompanyFile) => {
        if (!it)
            return;
        dataApi.companyFileDataGet(it.ID, it.NAME)
            .catch((err) => errorNotify(err, toast));
    }, [toast]);

    const prepareInputOperator = useCallback((item: CompanyOperator | null) => {
        setInputParamsOperator({
            id: item ? item.ID : null,
            userId: item ? item.USER_ID : null,
            roleType: item ? item.ROLE_TYPE : "READ",
            codeInn: item ? item.CODE_INN : "",
        });
    }, []);
    const createOrUpdateOperator = useCallback(() => {
        if (!company || !inputParamsOperator.codeInn || !inputParamsOperator.roleType)
            return;
        if (!inputParamsOperator.userId) {
            const p = usersForOperator.find(o => o.CODE_INN === inputParamsOperator.codeInn);
            if (p)
                inputParamsOperator.userId = p.USER_ID;
        }
        dataApi.companyOperatorCreateOrUpdate(inputParamsOperator.id, company.ID, inputParamsOperator.codeInn, inputParamsOperator.roleType, inputParamsOperator.userId)
            .then((value) => {
                toast({title: "Save", status: "success"});
                setCompanyOperatorList(prev => {
                    const lst = prev ? [...prev] : [];
                    let findO = lst.find(o => o.ID === value.ID);
                    if (findO) {
                        lst[lst.indexOf(findO)] = value;
                    } else {
                        lst.push(value);
                    }
                    return lst;
                });
                if (!inputParamsOperator.id) {
                    setInputParamsOperator(prev => {
                        return {...prev, id: value.ID};
                    });
                }
            })
            .catch((err) => errorNotify(err, toast));
    }, [inputParamsOperator, company, toast, setCompanyOperatorList, usersForOperator]);

    const getFIO = useCallback((item: CompanyOperator): string => {
        if (!item.USER_ID || !usersForOperator)
            return "";
        const p = usersForOperator.find(o => o.USER_ID === item.USER_ID);
        return p ? `${p.FIRST_NAME} ${p.SECOND_NAME} ${p.LAST_NAME}` : "";
    }, [usersForOperator]);

    return (
        <Box borderWidth={1} borderColor="gray">
            <Heading size="xl">Компания {company ? company.ID : ""}</Heading>
            <Tabs.Root defaultValue="Info">
                <Tabs.List>
                    <Tabs.Trigger value="Info">
                        <LuInfo/>
                        Основная информация
                    </Tabs.Trigger>
                    <Tabs.Trigger value="Balans">
                        <LuCoins/>
                        Баланс
                    </Tabs.Trigger>
                    <Tabs.Trigger value="Documents">
                        <LuLibrary/>
                        Документы
                    </Tabs.Trigger>
                    <Tabs.Trigger value="Emploers">
                        <LuUsers/>
                        Сотрудники
                    </Tabs.Trigger>
                    <Tabs.Trigger value="Settings">
                        <LuSettings/>
                        Настройки
                    </Tabs.Trigger>
                </Tabs.List>
                <Tabs.Content value="Info">
                    <Box borderWidth={1} borderColor="gray">
                        <Heading>Данные компании</Heading>
                        <SimpleGrid columns={[2]} gap={3}>
                            <Box>
                                <Text>Полное название</Text>
                            </Box>
                            <Box>
                                <Text>{company ? company.NAME_FULL : ""}</Text>
                            </Box>
                            <Box>
                                <Text>ИНН</Text>
                            </Box>
                            <Box>
                                <Text>{company ? company.CODE_INN : ""}</Text>
                            </Box>
                            <Box>
                                <Text>КПП</Text>
                            </Box>
                            <Box>
                                <Text>{company ? company.CODE_KPP : ""}</Text>
                            </Box>
                            <Box>
                                <Text>ОГРН</Text>
                            </Box>
                            <Box>
                                <Text>{company ? company.CODE_OGRN : ""}</Text>
                            </Box>
                            <Box>
                                <Text>Юридический адресс</Text>
                            </Box>
                            <Box>
                                <Text>{company ? company.ADDRESS : ""}</Text>
                            </Box>
                            <Box>
                                <Text>Фактический адресс</Text>
                            </Box>
                            <Box>
                                <Text>{company ? company.ADDRESS_ACTUAL : ""}</Text>
                            </Box>
                        </SimpleGrid>
                    </Box>
                </Tabs.Content>
                <Tabs.Content value="Balans">
                    <Center>
                        <Text fontSize="xl">{account ? `Баланс=${account.BALANCE} резерв=${account.RESERVED}` : ""}</Text>
                    </Center>
                </Tabs.Content>
                <Tabs.Content value="Documents">
                    <Box borderWidth={1} borderColor="gray">
                        <Table.Root size="sm" variant="outline" striped>
                            <Table.Header>
                                <Table.Row>
                                    <Table.ColumnHeader>Id</Table.ColumnHeader>
                                    <Table.ColumnHeader>Имя</Table.ColumnHeader>
                                    <Table.ColumnHeader>Размер</Table.ColumnHeader>
                                    <Table.ColumnHeader>Хеш</Table.ColumnHeader>
                                    <Table.ColumnHeader>Создан</Table.ColumnHeader>
                                    <Table.ColumnHeader>Изменен</Table.ColumnHeader>
                                    <Table.ColumnHeader>Действия</Table.ColumnHeader>
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                {files.map((item) => (
                                    <Table.Row key={item.ID}>
                                        <Table.Cell>{item.ID}</Table.Cell>
                                        <Table.Cell>{item.NAME}</Table.Cell>
                                        <Table.Cell>{item.CONTENT_SIZE}</Table.Cell>
                                        <Table.Cell>{item.CONTENT_HASH}</Table.Cell>
                                        <Table.Cell>{item.CREATED ? DateTime.fromMillis(item.CREATED).toFormat('HH:mm:ss dd.MM.yyyy') : ""}</Table.Cell>
                                        <Table.Cell>{item.VERSION ? DateTime.fromMillis(item.VERSION).toFormat('HH:mm:ss dd.MM.yyyy') : ""}</Table.Cell>
                                        <Table.Cell>
                                            <Button onClick={e => fileDownload(item)}>
                                                Скачать
                                            </Button>
                                        </Table.Cell>
                                    </Table.Row>
                                ))}
                            </Table.Body>
                        </Table.Root>
                    </Box>
                </Tabs.Content>
                <Tabs.Content value="Emploers">
                    <Box borderWidth={1} borderColor="gray">
                        <Box>
                            <Button onClick={e => {
                                if (showFormOperator) {
                                    setShowFormOperator(false);
                                } else {
                                    prepareInputOperator(null);
                                    setShowFormOperator(true);
                                }
                            }}>
                                Создать новый
                            </Button>
                        </Box>
                        <Show when={showFormOperator}>
                            <Heading size="xl">Оператор {inputParamsOperator.id}</Heading>
                            <Box borderWidth={1} borderColor="gray">
                                <Field.Root orientation="horizontal" required>
                                    <Field.Label>ИНН <Field.RequiredIndicator/></Field.Label>
                                    <Input type="text" placeholder="ИНН"
                                           value={inputParamsOperator.codeInn}
                                           onChange={(it) => setInputParamsOperator((prev) => {
                                               return {...prev, codeInn: it.target.value}
                                           })}/>
                                </Field.Root>
                                <Field.Root orientation="horizontal" required>
                                    <Field.Label>Роль <Field.RequiredIndicator/></Field.Label>
                                    <Select.Root
                                        value={[inputParamsOperator.roleType]}
                                        // defaultValue={[String(inputParams.sex)]}
                                        collection={companyOperatorRoleTypeList}
                                        onValueChange={(e: any) => setInputParamsOperator(prev => {
                                            return {
                                                ...prev, roleType: e.value[0]
                                            }
                                        })}>
                                        <Select.HiddenSelect/>
                                        <Select.Control>
                                            <Select.Trigger>
                                                <Select.ValueText placeholder="Роль"/>
                                            </Select.Trigger>
                                            <Select.IndicatorGroup>
                                                <Select.Indicator/>
                                            </Select.IndicatorGroup>
                                        </Select.Control>
                                        <Select.Positioner>
                                            <Select.Content>
                                                {companyOperatorRoleTypeList.items.map((it: any) => (
                                                    <Select.Item item={it} key={it.value}>
                                                        {it.label}
                                                        <Select.ItemIndicator/>
                                                    </Select.Item>
                                                ))}
                                            </Select.Content>
                                        </Select.Positioner>
                                    </Select.Root>
                                </Field.Root>
                                {/*
                                <Field.Root orientation="horizontal" required>
                                    <Field.Label>Пользователь </Field.Label>
                                    <Select.Root
                                        value={[inputParamsOperator.userId ? String(inputParamsOperator.userId) : ""]}
                                        // defaultValue={[String(inputParams.userId)]}
                                        collection={usersForOperatorList}
                                        onValueChange={(e: any) => setInputParamsOperator(prev => {
                                            return {
                                                ...prev, userId: e.value[0] && e.value[0] !== "" ? parseInt(e.value[0]) : null
                                            }
                                        })}>
                                        <Select.HiddenSelect/>
                                        <Select.Control>
                                            <Select.Trigger>
                                                <Select.ValueText placeholder="Оператор"/>
                                            </Select.Trigger>
                                            <Select.IndicatorGroup>
                                                <Select.Indicator/>
                                            </Select.IndicatorGroup>
                                        </Select.Control>
                                        <Select.Positioner>
                                            <Select.Content>
                                                {usersForOperatorList.items.map((it: any) => (
                                                    <Select.Item item={it} key={it.value}>
                                                        {it.label}
                                                        <Select.ItemIndicator/>
                                                    </Select.Item>
                                                ))}
                                            </Select.Content>
                                        </Select.Positioner>
                                    </Select.Root>
                                </Field.Root>
*/}
                                <Button borderRadius={0} variant="solid" colorScheme="teal" width="full"
                                        onClick={e => createOrUpdateOperator()}>
                                    {inputParamsOperator.id ? "Сохранить" : "Создать"}
                                </Button>
                            </Box>
                        </Show>
                        <Table.Root size="sm" variant="outline" striped>
                            <Table.Header>
                                <Table.Row>
                                    <Table.ColumnHeader>ИНН</Table.ColumnHeader>
                                    <Table.ColumnHeader>ФИО</Table.ColumnHeader>
                                    <Table.ColumnHeader>Роль</Table.ColumnHeader>
                                    <Table.ColumnHeader>Создан</Table.ColumnHeader>
                                    <Table.ColumnHeader>Изменен</Table.ColumnHeader>
                                    <Table.ColumnHeader>Действия</Table.ColumnHeader>
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                {companyOperatorList.map((item) => (
                                    <Table.Row key={item.ID}>
                                        <Table.Cell>{item.CODE_INN}</Table.Cell>
                                        <Table.Cell>{getFIO(item)}</Table.Cell>
                                        <Table.Cell>{item.ROLE_TYPE}</Table.Cell>
                                        <Table.Cell>{item.CREATED ? DateTime.fromMillis(item.CREATED).toFormat('HH:mm:ss dd.MM.yyyy') : ""}</Table.Cell>
                                        <Table.Cell>{item.VERSION ? DateTime.fromMillis(item.VERSION).toFormat('HH:mm:ss dd.MM.yyyy') : ""}</Table.Cell>
                                        <Table.Cell>
                                            <Show when={checkAccessOwner}>
                                                <Button onClick={e => {
                                                    prepareInputOperator(item);
                                                    setShowFormOperator(true);
                                                }}>Редактировать</Button>
                                            </Show>
                                        </Table.Cell>
                                    </Table.Row>
                                ))}
                            </Table.Body>
                        </Table.Root>
                    </Box>
                </Tabs.Content>
                <Tabs.Content value="Settings">
                    <Center>
                        <Stack>
                            <Field.Root orientation="horizontal">
                                <Field.Label>id <Field.RequiredIndicator/></Field.Label>
                                <Input type="tel" placeholder="id"
                                       disabled={true}
                                       value={inputParams.id ? inputParams.id : ""}
                                       onChange={(it) => setInputParams((prevState) => {
                                           return {...prevState, id: parseInt(it.target.value)}
                                       })}/>
                            </Field.Root>
                            <Field.Root orientation="horizontal">
                                <Field.Label>Имя <Field.RequiredIndicator/></Field.Label>
                                <Input type="tel" placeholder="Имя"
                                       disabled={true}
                                       value={inputParams.name}
                                       onChange={(it) => setInputParams((prevState) => {
                                           return {...prevState, name: it.target.value}
                                       })}/>
                            </Field.Root>
                            <Field.Root orientation="horizontal" required>
                                <Field.Label>Юридический адресс <Field.RequiredIndicator/></Field.Label>
                                <Input type="text" placeholder="Юридический адресс"
                                       value={inputParams.address}
                                       onChange={(it) => setInputParams((prevState) => {
                                           return {...prevState, address: it.target.value}
                                       })}/>
                            </Field.Root>
                            <Field.Root orientation="horizontal" required>
                                <Field.Label>Фактический адресс <Field.RequiredIndicator/></Field.Label>
                                <Input type="text" placeholder="Фактический адресс"
                                       value={inputParams.addressActual}
                                       onChange={(it) => setInputParams((prevState) => {
                                           return {...prevState, addressActual: it.target.value}
                                       })}/>
                            </Field.Root>
                            <Field.Root orientation="horizontal">
                                <Field.Label>ИНН <Field.RequiredIndicator/></Field.Label>
                                <Input type="text" placeholder="ИНН"
                                       disabled={true}
                                       value={inputParams.codeInn}
                                       onChange={(it) => setInputParams((prevState) => {
                                           return {...prevState, codeInn: it.target.value}
                                       })}/>
                            </Field.Root>
                            <Field.Root orientation="horizontal" required>
                                <Field.Label>Email <Field.RequiredIndicator/></Field.Label>
                                <Input type="email" placeholder="Email"
                                       value={inputParams.email}
                                       onChange={(it) => setInputParams((prevState) => {
                                           return {...prevState, email: it.target.value}
                                       })}/>
                            </Field.Root>
                            <Field.Root orientation="horizontal" required>
                                <Field.Label>Телефон <Field.RequiredIndicator/></Field.Label>
                                <Input type="tel" placeholder="Телефон"
                                       value={inputParams.tel}
                                       onChange={(it) => setInputParams((prevState) => {
                                           return {...prevState, tel: it.target.value}
                                       })}/>
                            </Field.Root>
                            <Button borderRadius={0} variant="solid" colorScheme="teal" width="full" onClick={save}>
                                Save
                            </Button>
                        </Stack>
                    </Center>
                </Tabs.Content>
            </Tabs.Root>
        </Box>
    );
};